'use client';

import { c as _c } from "react/compiler-runtime";
import { assertUnreachable } from '@bridge/utils';
import { useFormContext } from 'react-hook-form';
import { Checkbox, DropzoneInput, FormField, Input, RadioGroupList, Select, Switch, Textarea, ToggleGroup } from '../components';
import { AmountInputField, CheckboxField, ColorInputField, CountrySelectField, CustomField, DatePickerField, DateTimeLocalField, FileField, InputField, MultipleSelectorField, MultiSelectCheckboxField, RadioGroupListField, SearchInputField, SelectField, SwitchField, TagInputField, TextareaField, ToggleGroupField, TotpInputField, UploadField } from './fields';
import type { ElementType, ReactElement } from 'react';
import type { Control, ControllerFieldState, ControllerRenderProps, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import type { UploadComponentProps } from './fields';
import type { FormFieldConfig, FormFieldConfigVariant, ResolvedComponentProps, SupportedComponentType, UploadProps } from './types';

// Define a mapping from string identifiers to component constructors
const componentMap: Record<SupportedComponentType, ElementType> = {
  amountInput: AmountInputField,
  checkbox: Checkbox,
  colorInput: ColorInputField,
  countrySelect: CountrySelectField,
  custom: CustomField,
  datePicker: DatePickerField,
  datetimeLocal: DateTimeLocalField,
  file: DropzoneInput,
  input: Input,
  multipleSelector: MultipleSelectorField,
  multiSelectCheckbox: MultiSelectCheckboxField,
  radioGroupList: RadioGroupList,
  searchInput: SearchInputField,
  select: Select,
  switch: Switch,
  tagInput: TagInputField,
  textarea: Textarea,
  toggleGroup: ToggleGroup,
  totpInput: TotpInputField,
  upload: UploadField
};
function resolveComponentProps<TFieldValues extends FieldValues, C extends SupportedComponentType>(fieldConfig: FormFieldConfigVariant<TFieldValues, C>, formContext: UseFormReturn<TFieldValues>): ResolvedComponentProps<TFieldValues, C> {
  const {
    componentProps
  } = fieldConfig;
  if (typeof componentProps === 'function') {
    return componentProps(formContext);
  }
  // Variants with required props always declare componentProps, so the {} default is never observed by them
  return componentProps ?? {} as ResolvedComponentProps<TFieldValues, C>;
}
interface DeclarativeFormFieldProps<TFieldValues extends FieldValues, TContext = unknown> {
  // Update fieldConfig to expect a string identifier for the component
  fieldConfig: FormFieldConfig<TFieldValues> & {
    component: SupportedComponentType;
  };
  control: Control<TFieldValues, TContext>;
  labelSize?: 'sm' | 'md' | 'lg';
  /** Form-level upload callbacks, merged into upload field componentProps */
  uploadProps?: UploadProps;
}
export function DeclarativeFormField(t0) {
  const $ = _c(13);
  const {
    fieldConfig,
    control,
    labelSize: t1,
    uploadProps
  } = t0;
  const labelSize = t1 === undefined ? "md" : t1;
  const InputComponent = componentMap[fieldConfig.component];
  const formContext = useFormContext();
  if (!InputComponent) {
    console.error(`Unsupported component identifier: ${fieldConfig.component}`);
    let t2;
    if ($[0] !== fieldConfig.component) {
      t2 = <>Error: Unsupported component type '{fieldConfig.component}'</>;
      $[0] = fieldConfig.component;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const t2 = fieldConfig.name as Path<TFieldValues>;
  let t3;
  if ($[2] !== InputComponent || $[3] !== control || $[4] !== fieldConfig || $[5] !== formContext || $[6] !== labelSize || $[7] !== uploadProps) {
    t3 = t4 => {
      const {
        field,
        fieldState
      } = t4;
      const commonFieldProps = {
        control,
        field,
        fieldState,
        fieldConfig,
        labelSize
      };
      switch (fieldConfig.component) {
        case "amountInput":
          {
            return <AmountInputField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "checkbox":
          {
            return <CheckboxField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "countrySelect":
          {
            return <CountrySelectField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "colorInput":
          {
            return <ColorInputField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "custom":
          {
            return <CustomField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "datePicker":
          {
            return <DatePickerField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "datetimeLocal":
          {
            return <DateTimeLocalField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "file":
          {
            return <FileField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "input":
          {
            return <InputField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} InputComponent={InputComponent} />;
          }
        case "multipleSelector":
          {
            return <MultipleSelectorField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "multiSelectCheckbox":
          {
            return <MultiSelectCheckboxField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "radioGroupList":
          {
            return <RadioGroupListField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "searchInput":
          {
            return <SearchInputField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "select":
          {
            return <SelectField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "switch":
          {
            return <SwitchField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "tagInput":
          {
            return <TagInputField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "textarea":
          {
            return <TextareaField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "toggleGroup":
          {
            return <ToggleGroupField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "totpInput":
          {
            return <TotpInputField {...commonFieldProps} componentProps={resolveComponentProps(fieldConfig, formContext)} />;
          }
        case "upload":
          {
            const mergedUploadProps = {
              ...uploadProps,
              ...resolveComponentProps(fieldConfig, formContext)
            };
            return <UploadField {...commonFieldProps} componentProps={mergedUploadProps} />;
          }
        default:
          {
            return assertUnreachable(fieldConfig);
          }
      }
    };
    $[2] = InputComponent;
    $[3] = control;
    $[4] = fieldConfig;
    $[5] = formContext;
    $[6] = labelSize;
    $[7] = uploadProps;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== control || $[10] !== t2 || $[11] !== t3) {
    t4 = <FormField control={control} name={t2} render={t3} />;
    $[9] = control;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}